import { useCallback, useContext, useMemo, type MouseEvent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import StandaloneFlightStateContext from 'contexts/Flights/StandaloneFlights/standaloneFlightsStateContext'
import { FlightsFareTypes } from 'constants/flight'
import { useAppDispatch } from 'hooks/reduxHooks'
import { editFlightsFromPreviousSegment } from 'actions/FlightNavigationActions'

const messages = defineMessages({
  editRouteLabel: {
    id: 'ccY1ox-4A3MG8_UzXelF',
    defaultMessage: 'Edit flight from {origin} to {destination}',
    description: 'Accessible label for returning to edit a previously selected flight route',
  },
  routeLabel: {
    id: '-ySnZEv0a_KDS31zVobo',
    defaultMessage: '{origin} - {destination}',
    description: 'Previously selected flight route shown in the compact flight selection stepper',
  },
  changeLabel: {
    id: '43hiQ7nLFlY5ikdCdydI',
    defaultMessage: 'Change',
    description: 'Hover label shown over a previously selected flight route that lets the user change that flight',
  },
})

const StepperContainer = styled(Group)`
  min-height: ${rem(48)};
  padding: ${rem(12)} 0;
  overflow-x: auto;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SelectedRouteButton = styled.button`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${rem(8)};
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
`

const CarrierLogo = styled(Image)`
  width: ${rem(24)};
  height: ${rem(24)};
  object-fit: contain;
`

const LabelSwap = styled.span`
  display: grid;
  justify-items: center;
  overflow: hidden;
`

const RouteText = styled(BodyText)`
  grid-area: 1 / 1;
  transition: transform 0.2s ease, opacity 0.2s ease;

  ${SelectedRouteButton}:hover & {
    transform: translateY(-100%);
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const ChangeText = styled(BodyText)`
  grid-area: 1 / 1;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 0.2s ease, opacity 0.2s ease;

  ${SelectedRouteButton}:hover & {
    transform: translateY(0);
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export interface FlightRouteStep {
  segment: string
  index: number
  flight: App.JourneyV2
  originCode: string
  destinationCode: string
}

interface FlightRouteStepperViewProps {
  selectedSegments: Array<FlightRouteStep>
  activeSegmentIndex: number
  fareType: string | undefined
  onChange: (stepIndex: number) => void
}

export function FlightRouteStepperView({ selectedSegments, activeSegmentIndex, fareType, onChange }: FlightRouteStepperViewProps) {
  const intl = useIntl()

  const activeStepLabel = useMemo(() => {
    if (fareType === FlightsFareTypes.RETURN) {
      return activeSegmentIndex === 0 ? intl.formatMessage({
        id: 'JrXxrOLU8pKoHZgJoW2m',
        defaultMessage: 'Choose departing flight',
        description: 'Active step label prompting the customer to choose the departing flight',
      }) : intl.formatMessage({
        id: 'a8IHlay9Rn5O0-7x2-nB',
        defaultMessage: 'Choose return flight',
        description: 'Active step label prompting the customer to choose the return flight',
      })
    }

    if (fareType === FlightsFareTypes.MULTI_CITY) {
      return intl.formatMessage({
        id: 'Et0LxIMi7wcR8FUVBFED',
        defaultMessage: 'Choose flight {flightNumber}',
        description: 'Active step label prompting the customer to choose the next flight in a multi-city search',
      }, { flightNumber: activeSegmentIndex + 1 })
    }

    return intl.formatMessage({
      id: 'LU3-P_Op3hl24C14Wf0B',
      defaultMessage: 'Choose flight',
      description: 'Active step label prompting the customer to choose a flight for a one-way search',
    })
  }, [activeSegmentIndex, fareType, intl])

  const handleSelectedRouteClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    onChange(Number(event.currentTarget.dataset.segmentIndex))
  }, [onChange])

  // The stepper only appears once a previous leg has been selected (e.g. the return step, or
  // later multi-city legs). The first/departing step shows no stepper - matching the design.
  if (!selectedSegments.length) {
    return null
  }

  return (
    <StepperContainer direction="horizontal" gap={8} verticalAlign="center">
      {selectedSegments.map(({ segment, index, flight, originCode, destinationCode }) => {
        const editRouteLabel = intl.formatMessage(messages.editRouteLabel, {
          origin: originCode,
          destination: destinationCode,
        })

        return (
          <Group key={segment} direction="horizontal" gap={8} verticalAlign="center">
            <SelectedRouteButton
              type="button"
              aria-label={editRouteLabel}
              data-segment-index={index}
              onClick={handleSelectedRouteClick}
            >
              <CarrierLogo
                image={{ url: flight.carrierBrandMark ?? flight.carrierLogo }}
                width={24}
                height={24}
                alt=""
              />
              <LabelSwap>
                <RouteText variant="medium">
                  {intl.formatMessage(messages.routeLabel, {
                    origin: originCode,
                    destination: destinationCode,
                  })}
                </RouteText>
                <ChangeText variant="medium" aria-hidden>
                  {intl.formatMessage(messages.changeLabel)}
                </ChangeText>
              </LabelSwap>
            </SelectedRouteButton>
            <LineAngleRightIcon size="XS" colour="light-foreground-alt" />
          </Group>
        )
      })}

      <BodyText variant="medium" aria-current="step">{activeStepLabel}</BodyText>
    </StepperContainer>
  )
}

export default function FlightRouteStepper() {
  const dispatch = useAppDispatch()
  const {
    activeSegmentKey,
    fareType,
    flightAirports,
    segments,
    selectedFlights,
  } = useContext(StandaloneFlightStateContext)

  const activeSegmentIndex = Math.max(segments.indexOf(activeSegmentKey), 0)
  const selectedSegments = useMemo<Array<FlightRouteStep>>(() => {
    return segments.slice(0, activeSegmentIndex).flatMap((segment, index) => {
      const flight = selectedFlights[segment]
      const airports = flightAirports[segment]

      return flight && airports ? [{
        segment,
        index,
        flight,
        originCode: airports.origin.code,
        destinationCode: airports.destination.code,
      }] : []
    })
  }, [activeSegmentIndex, flightAirports, segments, selectedFlights])

  const handleChange = useCallback((stepIndex: number) => {
    dispatch(editFlightsFromPreviousSegment(stepIndex))
  }, [dispatch])

  return <FlightRouteStepperView
    selectedSegments={selectedSegments}
    activeSegmentIndex={activeSegmentIndex}
    fareType={fareType}
    onChange={handleChange}
  />
}
